/*
 * Office.js document I/O, ported verbatim from taskpane.js.
 * These wrap the callback-based getFileAsync / Word.run primitives so the rest
 * of the app can await plain Promises. The byte-slice merge + single base64
 * encode is correctness-critical (see comments) and is kept unchanged.
 */

function byteSlicesToBase64(slices: Uint8Array[], totalBytes: number): string {
  const merged = new Uint8Array(totalBytes);
  let offset = 0;
  slices.forEach((slice) => {
    merged.set(slice, offset);
    offset += slice.byteLength;
  });

  const chunkSize = 32766;
  let base64 = "";
  for (let start = 0; start < merged.byteLength; start += chunkSize) {
    const chunk = merged.subarray(start, start + chunkSize);
    let binary = "";
    for (let i = 0; i < chunk.byteLength; i++) {
      binary += String.fromCharCode(chunk[i]);
    }
    base64 += window.btoa(binary);
  }
  return base64;
}

/** Read the full DOCX as base64 via Office.js, merging slices before encoding. */
export function getDocumentAsBase64(): Promise<string> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 65536 },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          return reject(new Error(result.error.message));
        }
        const file = result.value;
        const slices: Uint8Array[] = [];
        let totalBytes = 0;
        let received = 0;

        function getSlice(idx: number): void {
          file.getSliceAsync(idx, (sr) => {
            if (sr.status !== Office.AsyncResultStatus.Succeeded) {
              file.closeAsync();
              return reject(new Error(sr.error.message));
            }
            /*
             * Office returns DOCX content in multiple byte slices. Encoding
             * each slice separately can place base64 padding in the middle of
             * the final string, so merge bytes first and encode once.
             */
            const bytes = Uint8Array.from(sr.value.data as number[]);
            slices[idx] = bytes;
            totalBytes += bytes.byteLength;
            received++;
            if (received === file.sliceCount) {
              file.closeAsync();
              resolve(byteSlicesToBase64(slices, totalBytes));
              return;
            }
            getSlice(received);
          });
        }
        getSlice(0);
      }
    );
  });
}

/** Replace the whole document body with corrected DOCX bytes (batch write). */
export async function insertCorrectedDocument(correctedBase64: string): Promise<void> {
  await Word.run(async (context) => {
    context.document.body.insertFileFromBase64(correctedBase64, Word.InsertLocation.replace);
    await context.sync();
  });
}

/** Read the currently selected text. */
export async function readSelectionText(): Promise<string> {
  return Word.run(async (ctx) => {
    const sel = ctx.document.getSelection();
    sel.load("text");
    await ctx.sync();
    // eslint-disable-next-line office-addins/call-sync-after-load, office-addins/call-sync-before-read
    return sel.text;
  });
}

/**
 * Read the text of the paragraph containing the cursor/selection.
 *
 * This runs inside the 1.25s live-feedback poll, so the paragraph text and
 * the selection fallback are loaded together in one sync round trip instead
 * of two or three sequential ones.
 */
export async function readCurrentParagraphText(): Promise<string> {
  return Word.run(async (ctx) => {
    const selection = ctx.document.getSelection();
    // eslint-disable-next-line office-addins/call-sync-before-read, office-addins/load-object-before-read
    const paragraphs = selection.paragraphs;
    paragraphs.load("items/text");
    selection.load("text");
    await ctx.sync();

    if (!paragraphs.items.length) {
      // eslint-disable-next-line office-addins/call-sync-after-load, office-addins/call-sync-before-read
      return selection.text || "";
    }

    return paragraphs.items[0].text || "";
  });
}

/** Snapshot every body paragraph's text, keyed by 1-based index. */
export async function readAllParagraphTexts(): Promise<Record<number, string>> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    /*
     * Loading only items/text in a single batch keeps the payload down to the
     * strings we need and halves the sync round trips on large documents.
     */
    paragraphs.load("items/text");
    await context.sync();
    const texts: Record<number, string> = {};
    for (let i = 0; i < paragraphs.items.length; i++) {
      texts[i + 1] = paragraphs.items[i].text;
    }
    return texts;
  });
}
